/* gsap-animations.js (smooth + calm)
   - Timeline: .lotus-timeline-section / .js-lotus-fade (unchanged)
   - Global card reveal: .gsap-card-reveal (container) + .js-gsap-card (items)
   - Uses ScrollTrigger.batch for subtle, unobtrusive reveals
   - Shopify editor safe (section load/unload)
*/

(() => {
  if (typeof window === "undefined") return;

  const TIMELINE_SECTION_SELECTOR = ".lotus-timeline-section";
  const TIMELINE_TARGET_SELECTOR = ".js-lotus-fade";

  // ✅ GLOBAL card reveal selectors (use these anywhere)
  const CARD_GRID_SELECTOR = ".gsap-card-reveal";
  const CARD_ITEM_SELECTOR = ".js-gsap-card";

  const triggersBySection = new WeakMap();

  const prefersReduced =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // ---------- Helpers ----------
  function qsa(root, sel) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function ensureGsap() {
    if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return false;
    gsap.registerPlugin(ScrollTrigger);
    return true;
  }

  function rememberTrigger(holderEl, trigger) {
    if (!holderEl || !trigger) return;
    var arr = triggersBySection.get(holderEl);
    if (!arr) arr = [];
    arr.push(trigger);
    triggersBySection.set(holderEl, arr);
  }

  function cleanup(holderEl) {
    const triggers = triggersBySection.get(holderEl);
    if (triggers && Array.isArray(triggers)) {
      triggers.forEach((t) => t?.kill?.());
    }
    triggersBySection.delete(holderEl);
  }

  // Calm reveal utility
  function reveal(batch, opts) {
    opts = opts || {};
    gsap.to(batch, {
      opacity: 1,
      y: 0,
      duration: opts.duration || 0.9,
      ease: opts.ease || "power2.out",
      stagger: opts.stagger || 0.07,
      overwrite: "auto",
      clearProps: "willChange",
    });
  }

  // ---------- Timeline (existing) ----------
  function initTimelineSection(sectionEl) {
    if (!sectionEl) return;

    const targets = qsa(sectionEl, TIMELINE_TARGET_SELECTOR);
    if (!targets.length) return;

    if (prefersReduced) {
      if (typeof gsap !== "undefined") gsap.set(targets, { opacity: 1, y: 0 });
      return;
    }

    if (!ensureGsap()) return;

    // allow re-init
    cleanup(sectionEl);

    gsap.set(targets, { opacity: 0, y: 14, willChange: "transform,opacity" });

    const st = ScrollTrigger.batch(targets, {
      start: "top 90%",
      end: "top 65%",
      scrub: 0.6,
      once: true,
      onEnter: function (batch) {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
          stagger: 0.14,
          overwrite: "auto",
          clearProps: "willChange",
        });
      },
      onLeaveBack: function (batch) {
        gsap.to(batch, {
          opacity: 0,
          y: 14,
          duration: 0.5,
          ease: "power2.out",
          overwrite: "auto",
        });
      },
    });

    if (Array.isArray(st)) st.forEach((t) => rememberTrigger(sectionEl, t));
    else rememberTrigger(sectionEl, st);
  }

  function initGsapTimeline(root) {
    qsa(root, TIMELINE_SECTION_SELECTOR).forEach(function (sectionEl) {
      if (sectionEl.dataset.gsapTimelineAnimated === "true") return;
      sectionEl.dataset.gsapTimelineAnimated = "true";
      initTimelineSection(sectionEl);
    });
  }

  // ---------- ✅ Global card reveals (use anywhere) ----------
  function initGsapCardReveals(root) {
    qsa(root, CARD_GRID_SELECTOR).forEach(function (grid) {
      if (grid.dataset.gsapCardRevealAnimated === "true") return;
      grid.dataset.gsapCardRevealAnimated = "true";

      var cards = qsa(grid, CARD_ITEM_SELECTOR);
      if (!cards.length) return;

      if (prefersReduced) {
        if (typeof gsap !== "undefined") gsap.set(cards, { opacity: 1, y: 0 });
        return;
      }

      if (!ensureGsap()) return;

      // allow re-init (theme editor)
      cleanup(grid);

      // initial state
      gsap.set(cards, { opacity: 0, y: 14, willChange: "transform,opacity" });

      // Start when EACH card hits viewport (batch trigger uses each card as trigger internally)
      var st = ScrollTrigger.batch(cards, {
        start: "top 90%",
        once: true,
        onEnter: function (batch) {
          reveal(batch, { stagger: 0.07, duration: 0.9, ease: "power2.out" });
        },
      });

      // Normalize storing
      if (Array.isArray(st)) {
        st.forEach(function (t) {
          rememberTrigger(grid, t);
        });
      } else {
        rememberTrigger(grid, st);
      }

      // layout shifts
      setTimeout(function () {
        if (typeof ScrollTrigger !== "undefined") ScrollTrigger.refresh();
      }, 250);
    });
  }

  // ---------- Init / Shopify editor ----------
  function initAll(root = document) {
    // include root itself if it matches (editor reloads)
    if (root.matches?.(TIMELINE_SECTION_SELECTOR)) initGsapTimeline(root.parentNode || document);
    if (root.matches?.(CARD_GRID_SELECTOR)) initGsapCardReveals(root.parentNode || document);

    initGsapTimeline(root);
    initGsapCardReveals(root);

    if (typeof ScrollTrigger !== "undefined") ScrollTrigger.refresh();
  }

  function findAnimatedHolder(target) {
    if (!target) return null;
    if (target.matches?.(TIMELINE_SECTION_SELECTOR) || target.matches?.(CARD_GRID_SELECTOR)) return target;
    return (
      target.querySelector?.(TIMELINE_SECTION_SELECTOR) ||
      target.querySelector?.(CARD_GRID_SELECTOR) ||
      null
    );
  }

  // normal load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Shopify editor support
  document.addEventListener("shopify:section:load", function (e) {
    initAll(e.target);
  });

  document.addEventListener("shopify:section:unload", function (e) {
    var holder = findAnimatedHolder(e.target);
    if (holder) cleanup(holder);
  });
})();
